import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getLessonSets, LessonSet, Lesson, getDifficultWordsCount } from '../services/api';
import './Lessons.css';

// Lesson icons (same as before)
const lessonIcons: Record<string, string> = {
  'Owoce': '🍎',
  'Warzywa': '🥕',
  'Jedzenie i napoje': '🍕',
  'Czynności - jedzenie': '🍴',
  'Zwierzęta domowe': '🐕',
  'Zwierzęta dzikie': '🦁',
  'Zwierzęta morskie': '🐠',
  'Czynności - zwierzęta': '🐾',
  'Rodzina': '👨‍👩‍👧',
  'Zawody i ludzie': '👷',
  'Części ciała': '🦵',
  'Czynności - własne ciało': '🤸',
  'Czynności - inni ludzie': '🤝',
  'Ubrania': '👕',
  'Ubrania i czynności': '👔',
  'Transport': '🚗',
  'Transport i czynności': '🚌',
  'Przedmioty codzienne': '🎒',
  'Dom i wyposażenie': '🛋️',
  'Dom i przedmioty': '🏠',
  'Ogród': '🌻',
  'Czynności - dom i ogród': '🧹',
  'Kształty': '⭐',
  'Sport': '⚽',
  'Poruszanie się': '🚶',
  'Wykonywanie pracy': '💼',
  'Geografia - podstawy': '🌍',
  'Geografia - trudniejsze': '🗺️',
  'Przestrzeń miejska': '🏙️',
  'Ruch uliczny': '🚦',
  'Kolory': '🎨',
  'Natura i pogoda': '🌳',
  'Przymiotniki': '📏',
  'Boże Narodzenie': '🎄',
  'Wielkanoc': '🐰',
  'Urodziny': '🎂',
  'Przyjęcia': '🎈',
  'Proste zdania - Powitania': '👋',
  'Proste zdania - Ja i Ty': '🙋',
  'Proste zdania - To jest...': '👆',
  'Proste zdania - Mam...': '🎁',
  'Proste zdania - Lubię...': '❤️',
  'Proste zdania - Mogę...': '💪',
  'Proste zdania - Gdzie jest...?': '🔍',
  'Proste zdania - Czynności': '🏃',
  'Proste zdania - Czas': '⏰',
  'Proste zdania - Miejsca': '📍',
  'Proste zdania - Pozycje': '📦',
  'Test 2 - Impreza i rodzina': '🎉',
  'Test 2 - Członkowie rodziny': '👨‍👩‍👧‍👦',
  'Test 2 - Ruchy ciała': '🤸',
  'Test 2 - Umiejętności': '💪',
  'Rozdział 1': '1️⃣',
  'Rozdział 2': '2️⃣',
  'Rozdział 3': '3️⃣',
  'Rozdział 4': '4️⃣',
  'Rozdział 5': '5️⃣',
  'Czas i data - miesiące': '📅',
  'Czas i data - tydzień': '📆',
  'Czas i data - pory roku': '🌸',
  'Czas i data - pory dnia': '🌅',
  'Czas i data - czas': '🕐',
  'Zjawiska pogodowe': '🌦️',
  'Zabawki': '🧸',
  'Gry i zabawy': '🎲',
  'Dom - budowa': '🏠',
};

const getIcon = (name: string): string => lessonIcons[name] || '📚';

const STORAGE_KEY = 'lessons_expanded_sets';

const loadExpandedSets = (): Set<string> => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      return new Set(JSON.parse(saved));
    }
  } catch (e) {
    console.error('Failed to load expanded sets:', e);
  }
  return new Set();
};

const saveExpandedSets = (sets: Set<string>) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(sets)));
  } catch (e) {
    console.error('Failed to save expanded sets:', e);
  }
};

const Lessons: React.FC = () => {
  const [sets, setSets] = useState<LessonSet[]>([]);
  const [expandedSets, setExpandedSets] = useState<Set<string>>(loadExpandedSets);
  const [difficultCount, setDifficultCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [setsData, difficultData] = await Promise.all([
          getLessonSets(),
          getDifficultWordsCount()
        ]);
        setSets(setsData);
        setDifficultCount(difficultData.count);
        // Expand first set by default only if nothing saved
        const saved = loadExpandedSets();
        if (saved.size === 0 && setsData.length > 0) {
          const initial = new Set([setsData[0].id]);
          setExpandedSets(initial);
          saveExpandedSets(initial);
        }
      } catch (err) {
        console.error('Failed to load lessons:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const toggleSet = (setId: string) => {
    setExpandedSets(prev => {
      const next = new Set(prev);
      if (next.has(setId)) {
        next.delete(setId);
      } else {
        next.add(setId);
      }
      saveExpandedSets(next);
      return next;
    });
  };

  const getCompletedCount = (lessons: Lesson[]) =>
    lessons.filter(l => l.times_completed > 0).length;

  const getTotalContent = (lessons: Lesson[]) =>
    lessons.reduce((sum, l) => sum + Number(l.word_count || 0) + Number(l.sentence_count || 0), 0);

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="lessons-page fade-in">
      <h1>📚 Lekcje</h1>
      <p className="subtitle">Wybierz lekcję, aby nauczyć się nowych słówek</p>

      {difficultCount > 0 && (
        <Link to="/difficult" className="difficult-words-banner">
          <span className="banner-icon">🔥</span>
          <div className="banner-text">
            <strong>Trudne słówka</strong>
            <span>Masz {difficultCount} słówek do powtórki</span>
          </div>
          <span className="banner-arrow">➜</span>
        </Link>
      )}

      <div className="lesson-sets">
        {sets.map((set) => {
          const isExpanded = expandedSets.has(set.id);
          const completedCount = getCompletedCount(set.lessons);
          const totalContent = getTotalContent(set.lessons);
          const hasLessons = set.lessons.length > 0;

          return (
            <div
              key={set.id}
              className={`lesson-set ${isExpanded ? 'expanded' : ''} ${!hasLessons ? 'empty' : ''}`}
              style={{ '--set-color': set.color } as React.CSSProperties}
            >
              <button
                className="set-header"
                onClick={() => hasLessons && toggleSet(set.id)}
                disabled={!hasLessons}
              >
                <span className="set-icon">{set.icon}</span>
                <div className="set-info">
                  <h2>{set.name_pl}</h2>
                  <span className="set-stats">
                    {hasLessons ? (
                      <>
                        {set.lessons.length} lekcji • {totalContent} elementów
                        {completedCount > 0 && ` • ✅ ${completedCount}/${set.lessons.length}`}
                      </>
                    ) : (
                      'Wkrótce...'
                    )}
                  </span>
                </div>
                {hasLessons && (
                  <span className={`set-arrow ${isExpanded ? 'open' : ''}`}>▼</span>
                )}
              </button>

              {isExpanded && hasLessons && (
                <div className="set-lessons">
                  {set.lessons.map((lesson) => (
                    <Link
                      key={lesson.id}
                      to={`/lessons/${lesson.id}`}
                      className="lesson-card"
                    >
                      <span className="lesson-icon">{getIcon(lesson.name_pl)}</span>
                      <div className="lesson-info">
                        <h3>{lesson.name_pl}</h3>
                        <div className="lesson-meta">
                          {lesson.word_count > 0 && (
                            <span className="word-count">📝 {lesson.word_count}</span>
                          )}
                          {lesson.sentence_count > 0 && (
                            <span className="sentence-count">💬 {lesson.sentence_count}</span>
                          )}
                          {lesson.times_completed > 0 && (
                            <span className="completed-badge">✅</span>
                          )}
                        </div>
                      </div>
                      <span className="lesson-arrow">➜</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Lessons;

